import { GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { Suggestion } from '@pepperhq/menu-sdk';
import { Option } from 'lib/types';
import { MuiModal } from 'ui/MuiModal';
import { SuggestionsForm } from './SuggestionsForm';
import React from 'react';
import { AutocompleteFormField } from 'lib/form/fields/AutocompleteFormField';
import { MuiForm } from 'lib/Form';

interface SuggestionsValueProps extends GridRenderEditCellParams {
    productOptions: Option[];
}

const SuggestionsValue: React.FC<SuggestionsValueProps> = ({ value, api, id, field, productOptions }) => {
    const [open, setOpen] = React.useState(() => {
        if (Array.isArray(value) && value.length) {
            return false;
        }
        return true;
    });
    const openByDefault = React.useMemo(() => {
        if (Array.isArray(value) && value.length) {
            return true;
        }
        return false;
    }, [value]);
    const [selectedSuggestion, setSelectedSuggestion] = React.useState<Suggestion | undefined>(undefined);
    const selectedSuggestions = React.useMemo(() => {
        const selectedOptions: Option[] = [];
        if (Array.isArray(value) && value.length && productOptions && productOptions.length) {
            for (const valueItem of value) {
                const productTitle = productOptions.find(
                    option => option.value === valueItem.productId
                )?.label;
                if (valueItem.type) {
                    switch (valueItem.type) {
                        case 'add':
                            selectedOptions.push({
                                label: `Add - ${productTitle}`,
                                value: valueItem.productId
                            });
                            break;
                        case 'replace':
                            selectedOptions.push({
                                label: `Replace - ${productTitle}`,
                                value: valueItem.productId
                            });
                            break;
                        case 'bogof':
                            selectedOptions.push({
                                label: 'Offer',
                                value: '__bogof__'
                            });
                            break;
                        default:
                            break;
                    }
                }
            }
        }
        selectedOptions.push({
            label: 'Add Product Suggestion',
            value: '__add__'
        });
        return selectedOptions;
    }, [value, productOptions]);

    const bogofExists = React.useMemo(() => {
        if (Array.isArray(value) && value.length) {
            return value.find((item: Suggestion) => item.type === 'bogof');
        }
        return false;
    }, [value]);

    const handleModalClose = React.useCallback(() => {
        setOpen(false);
        setSelectedSuggestion(undefined);
        api.stopCellEditMode({ id, field });
    }, [api, field, id]);
    const handleSave = React.useCallback(
        (suggestion: Suggestion) => {
            let updatedSuggestions: Suggestion[] = [];

            if (Array.isArray(value) && value.length) {
                // If saving a bogof suggestion
                if (suggestion.type === 'bogof') {
                    // Remove any existing bogof suggestions and add the new one
                    updatedSuggestions = value.filter((item: Suggestion) => item.type !== 'bogof');
                    updatedSuggestions.push(suggestion);
                } else {
                    // For non-bogof suggestions, check if we're editing an existing one
                    const existingIndex = value.findIndex(
                        (item: Suggestion) =>
                            item.productId === selectedSuggestion?.productId &&
                            item.type === selectedSuggestion?.type
                    );

                    if (existingIndex >= 0) {
                        // Update existing suggestion
                        updatedSuggestions = [...value];
                        updatedSuggestions[existingIndex] = suggestion;
                    } else {
                        // Add new suggestion
                        updatedSuggestions = [...value, suggestion];
                    }
                }
            } else {
                // No existing suggestions, just add the new one
                updatedSuggestions = [suggestion];
            }

            api.setEditCellValue({ id, field, value: updatedSuggestions });
            setOpen(false);
        },
        [api, field, id, value, selectedSuggestion]
    );
    const handleRemove = React.useCallback(() => {
        if (Array.isArray(value) && value.length && selectedSuggestion) {
            // Filter out the selected suggestion
            const remainingSuggestions = value.filter(
                (item: Suggestion) =>
                    !(
                        item.productId === selectedSuggestion.productId &&
                        item.type === selectedSuggestion.type
                    )
            );

            // If no suggestions left, set to null, otherwise set to remaining suggestions
            if (remainingSuggestions.length === 0) {
                api.setEditCellValue({ id, field, value: null });
            } else {
                api.setEditCellValue({ id, field, value: remainingSuggestions });
            }
        }
        setOpen(false);
        setSelectedSuggestion(undefined);
    }, [api, field, id, value, selectedSuggestion]);

    const handleChange = React.useCallback(
        (selectedValue: any) => {
            if (selectedValue && typeof selectedValue === 'string') {
                // Check if "New" option was selected
                if (selectedValue === '__add__') {
                    setSelectedSuggestion(undefined);
                    setOpen(true);
                    return;
                }
                if (selectedValue === '__bogof__') {
                    // We only allow one BOGOF suggestion
                    const bogofSuggestion = value.find((item: Suggestion) => item.type === 'bogof');
                    setSelectedSuggestion(bogofSuggestion);
                    setOpen(true);
                    return;
                }

                // Find the suggestion that matches the selected product ID
                if (Array.isArray(value) && value.length) {
                    const selected = value.find((item: Suggestion) => item.productId === selectedValue);
                    if (selected) {
                        setSelectedSuggestion(selected);
                        setOpen(true);
                    }
                }
            }
        },
        [value]
    );
    const onSubmit = React.useCallback(() => {
        api.stopCellEditMode({ id, field });
    }, [api, field, id]);

    const initialValues = React.useMemo(
        () => ({
            suggestion: ''
        }),
        []
    );

    return (
        <>
            <MuiForm initialValues={initialValues} onSubmit={onSubmit}>
                <AutocompleteFormField
                    onSelect={handleChange}
                    openByDefault={openByDefault}
                    name="suggestion"
                    options={selectedSuggestions}
                    multiple={false}
                    size="small"
                    textFieldVariant="standard"
                    inputProps={{
                        sx: { fontSize: '0.85rem', marginLeft: 1 }
                    }}
                />
            </MuiForm>
            <MuiModal open={open} onClose={handleModalClose}>
                <SuggestionsForm
                    productOptions={productOptions}
                    bogofExists={bogofExists}
                    suggestion={selectedSuggestion}
                    onRemove={handleRemove}
                    onClose={handleModalClose}
                    onSave={handleSave}
                />
            </MuiModal>
        </>
    );
};

export const SuggestionsEditCell = (props: GridRenderEditCellParams, productOptions: Option[]) => (
    <SuggestionsValue {...props} productOptions={productOptions} />
);
